import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Select,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Copy } from 'lucide-react';
import { adminApi, ApiError, type AdminReview } from '../../lib/api';
import { appLocale } from '../../lib/format';

// App review access (M56, docs/app-review-access.md D202–D205): the review
// family, its demo members, and review links for Apple's and Google's
// reviewers. A link's address is shown once, when it's made.

/** The small tag on demo accounts and the review family (D202). */
export function DemoTag() {
  return (
    <Badge ml={2} colorScheme="purple" variant="subtle" fontSize="0.7em">
      Demo
    </Badge>
  );
}

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(appLocale()) : 'never';

const STATUS: Record<
  AdminReview['links'][number]['status'],
  { label: string; scheme: string }
> = {
  active: { label: 'Active', scheme: 'green' },
  expired: { label: 'Expired', scheme: 'gray' },
  revoked: { label: 'Revoked', scheme: 'red' },
};

function Card({ children }: { children: ReactNode }) {
  return (
    <Box
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={4}
    >
      {children}
    </Box>
  );
}

export function ReviewTab() {
  const toast = useToast();
  const [review, setReview] = useState<AdminReview | null>(null);
  const [busy, setBusy] = useState(false);
  const [memberId, setMemberId] = useState('');
  const [label, setLabel] = useState('');
  const [spareName, setSpareName] = useState('');
  // The address of the link just made: shown once (D203).
  const [made, setMade] = useState<{ url: string; who: string } | null>(null);

  const [failed, setFailed] = useState<unknown>(null);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const res = await adminApi.getReview();
      setReview(res.data);
      setMemberId((current) => current || res.data.members[0]?.id || '');
    } catch (err) {
      setFailed(err);
      toast({
        title: 'Could not load review access',
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (work: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await work();
      await load();
      if (done) toast({ title: done, status: 'success', duration: 2000 });
    } catch (err) {
      toast({
        title: 'Could not save',
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied', status: 'success', duration: 1500 });
    } catch {
      toast({ title: 'Copy it by hand', status: 'info', duration: 2000 });
    }
  };

  const loadingState = useLoadingState({
    loading: !review && !failed,
    hasData: review !== null,
    error: failed,
    retry: () => void load(),
  });
  if (!review || loadingState.show)
    return <LoadingPanel state={loadingState} />;

  if (!review.family) {
    return (
      <Card>
        <VStack align="stretch" spacing={3}>
          <Text fontWeight={800} fontSize="18px">
            Review access
          </Text>
          <Text color="muted">
            Apple and Google reviewers can’t make a passkey or get an invite.
            Make a review family with demo members, then a review link for each
            reviewer to open. The text for the store forms is in
            docs/store-review-notes.md.
          </Text>
          <Button
            alignSelf="flex-start"
            colorScheme="brand"
            isLoading={busy}
            onClick={() =>
              void act(
                () => adminApi.createReviewFamily(),
                'Review family made'
              )
            }
          >
            Make the review family
          </Button>
        </VStack>
      </Card>
    );
  }

  const nameOf = (id: string) =>
    review.members.find((m) => m.id === id)?.displayName ?? '';

  return (
    <VStack align="stretch" spacing={4}>
      <Card>
        <VStack align="stretch" spacing={2}>
          <HStack>
            <Text fontWeight={800} fontSize="18px">
              {review.family.name}
            </Text>
            <DemoTag />
          </HStack>
          <Text fontSize="sm" color="muted">
            Demo members can only ever be in this family and aren’t counted
            anywhere. Delete the family in the Families tab to start over; that
            signs every review session out.
          </Text>
          <VStack align="stretch" spacing={1} pt={1}>
            {review.members.map((m) => (
              <HStack key={m.id} justify="space-between" flexWrap="wrap">
                <Text>
                  {m.displayName}{' '}
                  <Text as="span" color="muted">
                    @{m.handle}
                  </Text>
                </Text>
                <Badge
                  colorScheme={
                    m.role === 'root'
                      ? 'purple'
                      : m.role === 'admin'
                        ? 'orange'
                        : 'gray'
                  }
                >
                  {m.role}
                </Badge>
              </HStack>
            ))}
          </VStack>
          <HStack pt={2} flexWrap="wrap">
            <Input
              size="sm"
              maxW="220px"
              placeholder="Another demo member’s name"
              value={spareName}
              onChange={(e) => setSpareName(e.target.value)}
            />
            <Button
              size="sm"
              isDisabled={!spareName.trim()}
              isLoading={busy}
              onClick={() =>
                void act(async () => {
                  await adminApi.addReviewMember(spareName.trim());
                  setSpareName('');
                }, 'Member added')
              }
            >
              Add member
            </Button>
          </HStack>
        </VStack>
      </Card>

      <Card>
        <VStack align="stretch" spacing={3}>
          <Text fontWeight={800}>New review link</Text>
          <Text fontSize="sm" color="muted">
            Opening it signs that browser or app in as the member, without a
            passkey. It works on several devices for 30 days, or until you
            revoke it.
          </Text>
          <HStack flexWrap="wrap" spacing={2}>
            <Select
              size="sm"
              maxW="200px"
              aria-label="Demo member"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
            >
              {review.members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName}
                </option>
              ))}
            </Select>
            <Input
              size="sm"
              maxW="200px"
              placeholder="Label, e.g. Apple"
              value={label}
              maxLength={60}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Button
              size="sm"
              colorScheme="brand"
              isDisabled={!memberId}
              isLoading={busy}
              onClick={() =>
                void act(async () => {
                  const res = await adminApi.createReviewLink(
                    memberId,
                    label.trim() || undefined
                  );
                  setMade({ url: res.data.url, who: nameOf(memberId) });
                  setLabel('');
                })
              }
            >
              Make link
            </Button>
          </HStack>
          {made && (
            <Box
              borderWidth="1px"
              borderColor="line"
              borderRadius="md"
              p={3}
              bg="canvas"
            >
              <Text fontSize="sm" mb={1}>
                {made.who}’s review link. It’s shown only now: copy it into the
                review notes.
              </Text>
              <HStack align="flex-start">
                <Text
                  fontFamily="mono"
                  fontSize="xs"
                  wordBreak="break-all"
                  flex="1"
                  data-testid="review-link-url"
                >
                  {made.url}
                </Text>
                <Button
                  size="xs"
                  leftIcon={<Copy size={14} />}
                  onClick={() => void copy(made.url)}
                >
                  Copy
                </Button>
              </HStack>
            </Box>
          )}
        </VStack>
      </Card>

      <VStack align="stretch" spacing={2}>
        <Text fontWeight={800}>Review links</Text>
        {review.links.length === 0 && (
          <Text color="muted">No review links yet.</Text>
        )}
        {review.links.map((link) => {
          const status = STATUS[link.status];
          return (
            <Card key={link.id}>
              <HStack
                justify="space-between"
                align="flex-start"
                flexWrap="wrap"
              >
                <Box>
                  <HStack>
                    <Text fontWeight={700}>
                      {link.displayName}
                      {link.label ? ` · ${link.label}` : ''}
                    </Text>
                    <Badge colorScheme={status.scheme}>{status.label}</Badge>
                  </HStack>
                  <Text fontSize="sm" color="muted">
                    {link.activeSessions} active{' '}
                    {link.activeSessions === 1 ? 'session' : 'sessions'} · last
                    used {when(link.lastUsedAt)}
                  </Text>
                  <Text fontSize="xs" color="muted">
                    Made {when(link.createdAt)} ·{' '}
                    {link.revokedAt
                      ? `revoked ${when(link.revokedAt)}`
                      : `ends ${when(link.expiresAt)}`}
                  </Text>
                </Box>
                {link.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    isLoading={busy}
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Revoke ${link.displayName}’s review link? Every device signed in with it is signed out.`
                        )
                      )
                        return;
                      void act(
                        () => adminApi.revokeReviewLink(link.id),
                        'Revoked'
                      );
                    }}
                  >
                    Revoke
                  </Button>
                )}
              </HStack>
            </Card>
          );
        })}
      </VStack>
    </VStack>
  );
}
